import { Component, OnInit } from '@angular/core';
import { SharedService } from '../../services/shared.service';
import { UserActionLogService } from '../../services/user-action-log.service';
import { IUserActionLogRowVo } from '../../interfaces/Invoice/iuser-action-log-row-vo';
import { IUserActionLogSearchRequest } from '../../interfaces/Invoice/iuser-action-log-search-request';
import { IUserActionLogIdName } from '../../interfaces/Invoice/iuser-action-log-filter-options';

@Component({
  selector: 'app-action-logs',
  templateUrl: './action-logs.component.html',
  styleUrls: ['./action-logs.component.css']
})
export class ActionLogsComponent implements OnInit {
  loading = false;
  showErrorMessage = false;
  errorMessage = '';

  rows: IUserActionLogRowVo[] = [];

  /** Distinct pharmacies present in UserActionLog. */
  pharmacyOptions: IUserActionLogIdName[] = [];
  /** Distinct facilities in log for the selected pharmacy. */
  filteredFacilities: IUserActionLogIdName[] = [];
  /** Distinct action codes in log. */
  actionCodeOptions: string[] = [];

  selectedPharmacyId: number | null = null;
  selectedFacilityId: number | null = null;
  selectedActionCode: string | null = null;

  filterPatientName = '';
  filterSummary = '';
  filterUserDisplayName = '';

  constructor(
    private sharedService: SharedService,
    private userActionLogService: UserActionLogService
  ) {}

  ngOnInit(): void {
    this.sharedService.reloadUserPrivileges();
    this.loadInitialFilterOptions();
  }

  /** Pharmacies + action codes from log; then first search. */
  loadInitialFilterOptions(): void {
    this.userActionLogService.getFilterOptions(null).subscribe({
      next: (raw) => {
        this.applyFilterOptionsResponse(raw, false);
        this.filteredFacilities = [];
        this.search();
      },
      error: () => {
        this.pharmacyOptions = [];
        this.actionCodeOptions = [];
        this.filteredFacilities = [];
        this.search();
      }
    });
  }

  private applyFilterOptionsResponse(raw: any, updateFacilitiesOnly: boolean): void {
    if (!updateFacilitiesOnly) {
      this.pharmacyOptions = this.normalizeIdNameList(raw.pharmacies || raw.Pharmacies);
      this.actionCodeOptions = raw.actionCodes || raw.ActionCodes || [];
    }
    this.filteredFacilities = this.normalizeIdNameList(raw.facilities || raw.Facilities);
  }

  private normalizeIdNameList(list: any[]): IUserActionLogIdName[] {
    if (!list || list.length === 0) {
      return [];
    }
    return list.map((x: any) => ({
      id: x.id != null ? x.id : x.Id,
      name: x.name != null ? x.name : x.Name
    }));
  }

  onPharmacySelectChange(): void {
    this.selectedFacilityId = null;
    this.search();
    if (this.selectedPharmacyId == null) {
      this.filteredFacilities = [];
      return;
    }
    this.userActionLogService.getFilterOptions(this.selectedPharmacyId).subscribe({
      next: (raw) => {
        this.applyFilterOptionsResponse(raw, true);
      },
      error: () => {
        this.filteredFacilities = [];
      }
    });
  }

  onFacilitySelectChange(): void {
    this.search();
  }

  onActionSelectChange(): void {
    this.search();
  }

  private nonEmpty(s: string): string | null {
    const t = (s || '').trim();
    return t.length > 0 ? t : null;
  }

  buildRequest(): IUserActionLogSearchRequest {
    const req: IUserActionLogSearchRequest = {
      maxRows: 500
    };
    if (this.selectedPharmacyId != null) {
      req.pharmacyId = this.selectedPharmacyId;
    }
    if (this.selectedFacilityId != null) {
      req.facilityId = this.selectedFacilityId;
    }
    if (this.selectedActionCode != null && String(this.selectedActionCode).trim().length > 0) {
      req.actionCode = String(this.selectedActionCode).trim();
    }
    const pt = this.nonEmpty(this.filterPatientName);
    const sm = this.nonEmpty(this.filterSummary);
    const un = this.nonEmpty(this.filterUserDisplayName);
    if (pt) {
      req.patientNameContains = pt;
    }
    if (sm) {
      req.actionSummaryContains = sm;
    }
    if (un) {
      req.userDisplayNameContains = un;
    }
    return req;
  }

  search(): void {
    this.loading = true;
    this.showErrorMessage = false;
    this.errorMessage = '';
    this.userActionLogService.search(this.buildRequest()).subscribe({
      next: (data) => {
        const list = data || [];
        this.rows = list.map((it) => this.normalizeRow(it));
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.rows = [];
        this.showErrorMessage = true;
        this.errorMessage =
          (err && err.error && err.error.message) || (err && err.message) || 'Failed to load action logs.';
      }
    });
  }

  clearFilters(): void {
    this.selectedPharmacyId = null;
    this.selectedFacilityId = null;
    this.selectedActionCode = null;
    this.filteredFacilities = [];
    this.filterPatientName = '';
    this.filterSummary = '';
    this.filterUserDisplayName = '';
    this.search();
  }

  private normalizeRow(it: any): IUserActionLogRowVo {
    return {
      id: it.id != null ? it.id : it.Id,
      userId: it.userId != null ? it.userId : it.UserId,
      userDisplayName: it.userDisplayName != null ? it.userDisplayName : it.UserDisplayName,
      createdDate: it.createdDate != null ? it.createdDate : it.CreatedDate,
      actionCode: it.actionCode != null ? it.actionCode : it.ActionCode,
      actionSummary: it.actionSummary != null ? it.actionSummary : it.ActionSummary,
      detailJson: it.detailJson != null ? it.detailJson : it.DetailJson,
      patientId: it.patientId != null ? it.patientId : it.PatientId,
      pharmacyId: it.pharmacyId != null ? it.pharmacyId : it.PharmacyId,
      facilityId: it.facilityId != null ? it.facilityId : it.FacilityId,
      success: it.success != null ? it.success : it.Success,
      errorMessage: it.errorMessage != null ? it.errorMessage : it.ErrorMessage,
      ipAddress: it.ipAddress != null ? it.ipAddress : it.IpAddress,
      pharmacyName: it.pharmacyName != null ? it.pharmacyName : it.PharmacyName,
      facilityName: it.facilityName != null ? it.facilityName : it.FacilityName,
      patientDisplayName: it.patientDisplayName != null ? it.patientDisplayName : it.PatientDisplayName
    };
  }
}
